/**
 * Address Utilities
 *
 * Functions for normalizing and comparing addresses to detect duplicates
 */

/**
 * Normalize an address for duplicate detection
 * - Converts to lowercase
 * - Removes punctuation and extra spaces
 * - Standardizes common abbreviations
 *
 * @param {string} address - The address to normalize
 * @returns {string} - Normalized address hash
 */
export const normalizeAddress = (address) => {
  if (!address || typeof address !== 'string') {
    return '';
  }

  let normalized = address.toLowerCase().trim();

  // Remove common punctuation
  normalized = normalized.replace(/[.,\-#]/g, ' ');

  // Standardize common abbreviations
  const replacements = {
    'street': 'st',
    'road': 'rd',
    'avenue': 'ave',
    'drive': 'dr',
    'lane': 'ln',
    'court': 'ct',
    'place': 'pl',
    'terrace': 'ter',
    'apartment': 'apt',
    'flat': 'flt',
  };

  Object.entries(replacements).forEach(([full, abbr]) => {
    const regex = new RegExp(`\\b${full}\\b`, 'g');
    normalized = normalized.replace(regex, abbr);
  });

  // Remove all spaces and convert to single string
  normalized = normalized.replace(/\s+/g, '');

  return normalized;
};

/**
 * Check if two addresses are likely the same
 *
 * @param {string} address1 - First address
 * @param {string} address2 - Second address
 * @returns {boolean} - True if addresses match
 */
export const addressesMatch = (address1, address2) => {
  const hash1 = normalizeAddress(address1);
  const hash2 = normalizeAddress(address2);
  return hash1 === hash2 && hash1 !== '';
};

/**
 * Generate address hash for a property
 * Combines address and postcode for uniqueness
 *
 * @param {Object} property - Property object with address and postcode
 * @returns {string} - Combined address hash
 */
export const generatePropertyHash = (property) => {
  const addressPart = normalizeAddress(property.address || '');
  const postcodePart = normalizeAddress(property.postcode || '');

  // Combine both for stronger duplicate detection
  return `${addressPart}|${postcodePart}`;
};

/**
 * Find duplicate properties within a given list
 * Groups properties by their addressHash
 *
 * @param {Array} properties - Array of property objects (must have addressHash or address+postcode)
 * @returns {Object} - { duplicateGroups: Array, uniqueIds: Set, duplicateIds: Set }
 */
export const findDuplicatesInList = (properties) => {
  if (!properties || !Array.isArray(properties)) {
    return { duplicateGroups: [], uniqueIds: new Set(), duplicateIds: new Set() };
  }

  // Group by addressHash (or generate one if missing)
  const hashMap = new Map();

  properties.forEach((property) => {
    // Use existing addressHash or generate one
    const hash = property.addressHash || generatePropertyHash(property);

    if (hash && hash !== '|') { // Skip empty hashes
      if (hashMap.has(hash)) {
        hashMap.get(hash).push(property);
      } else {
        hashMap.set(hash, [property]);
      }
    }
  });

  // Build result
  const duplicateGroups = [];
  const uniqueIds = new Set();
  const duplicateIds = new Set();

  hashMap.forEach((props, hash) => {
    if (props.length > 1) {
      // Sort by dateAdded (newest first) or by data completeness
      const sorted = props.sort((a, b) => {
        // Prefer properties with Land Registry data
        const aHasLR = a.landRegistryRecord ? 1 : 0;
        const bHasLR = b.landRegistryRecord ? 1 : 0;
        if (bHasLR !== aHasLR) return bHasLR - aHasLR;

        // Then by dateAdded (newest first)
        const dateA = a.dateAdded?.toDate?.() || a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.dateAdded?.toDate?.() || b.createdAt?.toDate?.() || new Date(0);
        return dateB - dateA;
      });

      duplicateGroups.push({
        hash,
        address: sorted[0].address,
        postcode: sorted[0].postcode,
        properties: sorted,
        keepId: sorted[0].id, // First one (best) to keep
        removeIds: sorted.slice(1).map(p => p.id), // Others to remove
      });

      // First property is unique (to keep), rest are duplicates
      uniqueIds.add(sorted[0].id);
      sorted.slice(1).forEach(p => duplicateIds.add(p.id));
    } else {
      // Single property - unique
      uniqueIds.add(props[0].id);
    }
  });

  return {
    duplicateGroups,
    uniqueIds,
    duplicateIds,
    totalDuplicates: duplicateIds.size,
    totalGroups: duplicateGroups.length,
  };
};
